import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getBitacora, createBitacora, marcarBitacoraLeido, atenderBitacora, createIncidenciaEscolarBulk, getAlumnos } from '../lib/api';
import { formatNombreCompleto } from '../lib/nombre';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, Eye, Check, CheckCircle, Notebook, Megaphone, 
  UsersThree, ChatCircle, Clock
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';

const TIPOS_BITACORA = [
  { value: 'incidencia', label: 'Incidencia Escolar', icon: Notebook },
  { value: 'acuerdo', label: 'Acuerdo', icon: Check },
  { value: 'reunion', label: 'Reunión', icon: UsersThree },
  { value: 'aviso_direccion', label: 'Aviso de Dirección', icon: Megaphone },
  { value: 'seguimiento', label: 'Seguimiento Operativo', icon: Clock }
];

const BitacoraPage = () => {
  const { user, hasPermission } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [activeTab, setActiveTab] = useState('todos');
  const [comentario, setComentario] = useState('');

  const [formData, setFormData] = useState({
    tipo: '',
    titulo: '',
    contenido: '',
    turno: '',
    grado: '',
    grupo: ''
  });

  // Incidencia escolar múltiple
  const [alumnoBuscar, setAlumnoBuscar] = useState('');
  const [alumnoOptions, setAlumnoOptions] = useState([]);
  const [alumnosSeleccionados, setAlumnosSeleccionados] = useState([]);
  const [gravedadIncidencia, setGravedadIncidencia] = useState('media');
  const [sancionIncidencia, setSancionIncidencia] = useState('');

  const buscarAlumnos = async (q) => {
    if (!q || q.trim().length < 2) { setAlumnoOptions([]); return; }
    try {
      const resp = await getAlumnos({ nombre: q, limit: 10 });
      setAlumnoOptions(resp.data.alumnos || []);
    } catch { /* ignore */ }
  };

  const toggleAlumno = (a) => {
    setAlumnosSeleccionados((prev) => {
      if (prev.find((x) => x.id === a.id)) return prev.filter((x) => x.id !== a.id);
      return [...prev, a];
    });
  };

  const fetchBitacora = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeTab !== 'todos') {
        params.tipo = activeTab;
      }
      
      const response = await getBitacora(params);
      setEntries(response.data);
    } catch (error) {
      toast.error('Error al cargar bitácora');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchBitacora();
  }, [fetchBitacora]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Modo múltiple para incidencias escolares con alumnos seleccionados
      if (formData.tipo === 'incidencia' && alumnosSeleccionados.length > 0) {
        if (formData.contenido.trim().length < 10) {
          toast.error('La descripción del incidente debe tener al menos 10 caracteres');
          return;
        }
        const resp = await createIncidenciaEscolarBulk({
          alumno_ids: alumnosSeleccionados.map((a) => a.id),
          descripcion: formData.contenido,
          gravedad: gravedadIncidencia,
          tipo: 'conflicto_alumnos',
          sancion: sancionIncidencia || undefined,
          titulo: formData.titulo || undefined,
          turno: formData.turno && formData.turno !== 'all' ? formData.turno : undefined,
        });
        toast.success(`Incidencia registrada — folio ${resp.data.folio}. ${resp.data.alumnos_afectados} alumnos afectados.`);
        setShowModal(false);
        resetForm();
        setAlumnosSeleccionados([]);
        setAlumnoBuscar('');
        setAlumnoOptions([]);
        setSancionIncidencia('');
        fetchBitacora();
        return;
      }
      await createBitacora(formData);
      toast.success('Entrada registrada');
      setShowModal(false);
      resetForm();
      fetchBitacora();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleMarcarLeido = async (id) => {
    try {
      await marcarBitacoraLeido(id);
      toast.success('Marcado como leído');
      fetchBitacora();
    } catch (error) {
      toast.error('Error al marcar como leído');
    }
  };

  const handleAtender = async (id) => {
    try {
      await atenderBitacora(id, comentario);
      toast.success('Marcado como atendido');
      setShowDetailModal(false);
      setComentario('');
      fetchBitacora();
    } catch (error) {
      toast.error('Error al atender');
    }
  };

  const resetForm = () => {
    setFormData({
      tipo: '',
      titulo: '',
      contenido: '',
      turno: '',
      grado: '',
      grupo: ''
    });
  };

  const getEstadoColor = (estado) => {
    const colors = {
      pendiente: 'bg-amber-100 text-amber-800',
      visto: 'bg-blue-100 text-blue-800',
      atendido: 'bg-green-100 text-green-800'
    };
    return colors[estado] || 'bg-gray-100 text-gray-800';
  };

  const getTipoIcon = (tipo) => {
    const tipoData = TIPOS_BITACORA.find(t => t.value === tipo);
    if (tipoData) {
      const Icon = tipoData.icon;
      return <Icon size={18} weight="duotone" />;
    }
    return <Notebook size={18} />;
  };

  const canCreateTipo = (tipo) => {
    if (hasPermission(['administrador'])) return true;
    if (hasPermission(['direccion']) && ['aviso_direccion', 'reunion', 'acuerdo'].includes(tipo)) return true;
    if (hasPermission(['prefectura']) && ['incidencia', 'seguimiento'].includes(tipo)) return true;
    return false;
  };

  const availableTipos = TIPOS_BITACORA.filter(t => canCreateTipo(t.value));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="bitacora-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">Bitácora</h1>
          <p className="text-muted-foreground">Registro de actividades y comunicados</p>
        </div>
        {availableTipos.length > 0 && (
          <Button 
            size="sm"
            onClick={() => { resetForm(); setShowModal(true); }}
            data-testid="add-bitacora"
          >
            <Plus size={16} className="mr-1" />
            Nueva Entrada
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="todos" className="text-sm">Todos</TabsTrigger>
          {TIPOS_BITACORA.map(tipo => (
            <TabsTrigger key={tipo.value} value={tipo.value} className="text-sm">
              {tipo.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Entries Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full flex justify-center py-12">
            <div className="spinner" />
          </div>
        ) : entries.length === 0 ? (
          <div className="col-span-full text-center py-12 text-muted-foreground">
            <Notebook size={48} className="mx-auto mb-3 opacity-50" />
            <p>No hay entradas en la bitácora</p>
          </div>
        ) : (
          entries.map((entry, index) => (
            <Card 
              key={entry.id} 
              className="card-hover cursor-pointer stagger-item"
              style={{ animationDelay: `${index * 0.05}s` }}
              onClick={() => { setSelectedEntry(entry); setShowDetailModal(true); }}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-primary/10 text-primary">
                      {getTipoIcon(entry.tipo)}
                    </div>
                    <div>
                      <CardTitle className="text-base font-medium line-clamp-1">
                        {entry.titulo}
                      </CardTitle>
                      <p className="text-xs text-muted-foreground">
                        {TIPOS_BITACORA.find(t => t.value === entry.tipo)?.label}
                      </p>
                    </div>
                  </div>
                  <Badge className={`text-xs ${getEstadoColor(entry.estado_lectura)}`}>
                    {entry.estado_lectura}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                  {entry.contenido}
                </p>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{entry.nombre_registrador}</span>
                  <span>{entry.fecha_registro && format(new Date(entry.fecha_registro), 'dd/MM/yyyy HH:mm')}</span>
                </div>
                {(entry.turno || entry.grado) && (
                  <div className="flex gap-2 mt-2">
                    {entry.turno && (
                      <Badge variant="outline" className="text-xs capitalize">
                        {entry.turno}
                      </Badge>
                    )}
                    {entry.grado && (
                      <Badge variant="outline" className="text-xs">
                        {entry.grado}° {entry.grupo}
                      </Badge>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Nueva Entrada de Bitácora</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Tipo *</Label>
              <Select 
                value={formData.tipo} 
                onValueChange={(v) => setFormData(p => ({ ...p, tipo: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  {availableTipos.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Título *</Label>
              <Input
                value={formData.titulo}
                onChange={(e) => setFormData(p => ({ ...p, titulo: e.target.value }))}
                placeholder="Título de la entrada"
                required
                data-testid="titulo-input"
              />
            </div>
            <div>
              <Label>Contenido *</Label>
              <Textarea
                value={formData.contenido}
                onChange={(e) => setFormData(p => ({ ...p, contenido: e.target.value }))}
                placeholder="Descripción detallada..."
                rows={4}
                required
                data-testid="contenido-input"
              />
            </div>

            {formData.tipo === 'incidencia' && (
              <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-3" data-testid="incidencia-bulk-section">
                <div>
                  <Label className="text-sm font-medium">Alumnos involucrados (selección múltiple, de cualquier grado/grupo)</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Al guardar se creará un registro en el expediente de CADA alumno con el mismo folio.
                  </p>
                  <Input
                    placeholder="Buscar por nombre o apellido…"
                    value={alumnoBuscar}
                    onChange={(e) => { setAlumnoBuscar(e.target.value); buscarAlumnos(e.target.value); }}
                    data-testid="buscar-alumno-incidencia"
                  />
                  {alumnoOptions.length > 0 && (
                    <div className="mt-1 max-h-40 overflow-y-auto rounded border bg-card shadow-sm">
                      {alumnoOptions.map((a) => {
                        const selected = alumnosSeleccionados.find((x) => x.id === a.id);
                        return (
                          <button
                            key={a.id}
                            type="button"
                            onClick={() => toggleAlumno(a)}
                            className={`w-full text-left px-3 py-1.5 text-sm hover:bg-muted flex items-center justify-between ${selected ? 'bg-primary/10' : ''}`}
                          >
                            <span>{formatNombreCompleto(a)} · <span className="text-xs text-muted-foreground">{a.matricula} · {a.grado}° {a.grupo} · {a.turno}</span></span>
                            {selected && <Check size={14} className="text-primary" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {alumnosSeleccionados.length > 0 && (
                  <div className="flex flex-wrap gap-1" data-testid="alumnos-seleccionados">
                    {alumnosSeleccionados.map((a) => (
                      <Badge key={a.id} variant="secondary" className="text-xs">
                        {a.matricula} · {a.grado}°{a.grupo}
                        <button type="button" onClick={() => toggleAlumno(a)} className="ml-1 hover:text-destructive">×</button>
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Gravedad</Label>
                    <Select value={gravedadIncidencia} onValueChange={setGravedadIncidencia}>
                      <SelectTrigger data-testid="gravedad-incidencia"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="leve">Leve</SelectItem>
                        <SelectItem value="media">Media</SelectItem>
                        <SelectItem value="grave">Grave</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Sanción (opcional)</Label>
                    <Input
                      value={sancionIncidencia}
                      onChange={(e) => setSancionIncidencia(e.target.value)}
                      placeholder="Ej. Suspensión 1 día"
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Turno</Label>
                <Select 
                  value={formData.turno} 
                  onValueChange={(v) => setFormData(p => ({ ...p, turno: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="matutino">Matutino</SelectItem>
                    <SelectItem value="vespertino">Vespertino</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Grado</Label>
                <Select 
                  value={formData.grado} 
                  onValueChange={(v) => setFormData(p => ({ ...p, grado: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="1">1°</SelectItem>
                    <SelectItem value="2">2°</SelectItem>
                    <SelectItem value="3">3°</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Grupo</Label>
                <Select 
                  value={formData.grupo} 
                  onValueChange={(v) => setFormData(p => ({ ...p, grupo: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="A">A</SelectItem>
                    <SelectItem value="B">B</SelectItem>
                    <SelectItem value="C">C</SelectItem>
                    <SelectItem value="D">D</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={!formData.tipo || !formData.titulo || !formData.contenido}>
                Registrar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {selectedEntry && getTipoIcon(selectedEntry.tipo)}
              {selectedEntry?.titulo}
            </DialogTitle>
          </DialogHeader>
          {selectedEntry && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={getEstadoColor(selectedEntry.estado_lectura)}>
                  {selectedEntry.estado_lectura}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {TIPOS_BITACORA.find(t => t.value === selectedEntry.tipo)?.label}
                </Badge>
                {selectedEntry.turno && (
                  <Badge variant="outline" className="text-xs capitalize">
                    Turno {selectedEntry.turno}
                  </Badge>
                )}
                {selectedEntry.grado && (
                  <Badge variant="outline" className="text-xs">
                    {selectedEntry.grado}° {selectedEntry.grupo}
                  </Badge>
                )}
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground">Contenido</p>
                <p className="text-sm whitespace-pre-wrap">{selectedEntry.contenido}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Registrado por</p>
                  <p className="font-medium">{selectedEntry.nombre_registrador}</p>
                  <p className="text-xs text-muted-foreground capitalize">{selectedEntry.rol_registrador}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Fecha</p>
                  <p className="font-medium">
                    {selectedEntry.fecha_registro && format(new Date(selectedEntry.fecha_registro), "d 'de' MMMM, yyyy HH:mm", { locale: es })}
                  </p>
                </div>
              </div>

              {/* Comments */}
              {selectedEntry.comentarios?.length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Comentarios</p>
                  <ScrollArea className="h-32">
                    <div className="space-y-2">
                      {selectedEntry.comentarios.map((c) => (
                        <div key={`${c.fecha}-${c.nombre}`} className="p-2 bg-muted rounded-md text-sm">
                          <p className="font-medium">{c.nombre}</p>
                          <p>{c.comentario}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {format(new Date(c.fecha), 'dd/MM/yyyy HH:mm')}
                          </p>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {/* Actions */}
              {selectedEntry.estado_lectura !== 'atendido' && (
                <div className="space-y-3 pt-4 border-t">
                  {selectedEntry.estado_lectura === 'pendiente' && (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => handleMarcarLeido(selectedEntry.id)}
                    >
                      <Eye size={16} className="mr-2" />
                      Marcar como leído
                    </Button>
                  )}
                  
                  <div>
                    <Label>Comentario para atender</Label>
                    <Textarea
                      value={comentario}
                      onChange={(e) => setComentario(e.target.value)}
                      placeholder="Agregar comentario (opcional)..."
                      rows={2}
                    />
                  </div>
                  <Button
                    className="w-full"
                    onClick={() => handleAtender(selectedEntry.id)}
                  >
                    <CheckCircle size={16} className="mr-2" />
                    Marcar como atendido
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BitacoraPage;
